import { Map } from 'immutable';
import noop from 'lodash/noop';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as Actions from '../../actions';
import GridItem from '../../components/lib/GridItem/GridItem';
import { messages } from '../../constants/Collection';
import { GridConstants } from '../../constants/Grid';
import { StationConstants } from '../../constants/StationConstants';
import { getInDomain } from '../../domains/externalUtils';
import { getArt } from '../../lib/ArtUtils';
import { getGenreEntities } from '../../lib/EntityUtils';
import { isAnonymousListener } from '../../lib/ListenerUtils';
import { getBackstageGenrePath, getStationDetailsPathForStationId } from '../../lib/RouteUtils';

// GenreStationGridItem is a more-or-less direct conversion of the T1/2 GenreGridItem into
// the new T3 grid item, for use in StationsYouMightLike and eventually in genre browse.
// This may be merged into StationGridItem in the future.
class GenreStationGridItem extends React.Component {
  static propTypes = {
    startStation: PropTypes.func.isRequired,
    pauseTrack: PropTypes.func.isRequired,
    playResumeSource: PropTypes.func.isRequired,
    musicId: PropTypes.string.isRequired,
    seoToken: PropTypes.string,
    stationId: PropTypes.string,
    stationCode: PropTypes.string.isRequired,
    genreName: PropTypes.string.isRequired,
    shouldResumePlayback: PropTypes.bool,
    isPlaying: PropTypes.bool,
    isPaused: PropTypes.bool,
    imgUrl: PropTypes.string,
    isAnonymous: PropTypes.bool.isRequired,
    logWebRegistrationEvent: PropTypes.func.isRequired,
    componentName: PropTypes.string,
    position: PropTypes.number,
    totalStations: PropTypes.number,
    isAdvertiserStation: PropTypes.bool,
    isOwnStation: PropTypes.bool,
  };

  static contextTypes = {
    router: PropTypes.object,
  };

  static defaultProps = {
    stationCode: StationConstants.MUSIC_ID,
  };

  onFirstLineClick = e => {
    e.preventDefault();

    if (!this.props.isAdvertiserStation) {
      if (this.props.isOwnStation) {
        this.context.router.push(getStationDetailsPathForStationId(this.props.stationId));
      }
      this.context.router.push(getBackstageGenrePath(this.props.seoToken));
    } else {
      this.startOrResumeStation();
    }
  };

  startStation = () => {
    if (this.props.isAnonymous) {
      this.props.logWebRegistrationEvent({
        registrationEventAction: 'anon_station_started_from_browse',
      });
    }

    return this.props.startStation({
      stationCodePrefix: this.props.stationCode,
      musicId: this.props.musicId,
      componentName: this.props.componentName ? this.props.componentName : 'GenreGridItem',
      stationPosition: this.props.position,
      stationListLength: this.props.totalStations,
      rerouteBasedOnTier: true,
    });
  };

  startOrResumeStation = () => {
    if (this.props.shouldResumePlayback) {
      this.props.playResumeSource({ id: this.props.stationId });
    } else {
      this.startStation();
    }
  };

  render() {
    return (
      <GridItem
        imgUrl={this.props.imgUrl}
        mainText={this.props.genreName}
        mainTextOnclick={this.onFirstLineClick}
        secondText={<FormattedMessage {...messages.station} />}
        playButtonOnclick={this.startOrResumeStation}
        pauseButtonOnclick={this.props.pauseTrack}
        isPlaying={this.props.isPlaying && !this.props.isPaused}
        isPaused={this.props.isPaused}
        isStation={true}
      />
    );
  }
}

export function mapStateToProps(state, ownProps) {
  const musicId = ownProps.musicId;
  const genre = getGenreEntities(state).get(musicId) || Map();
  const station = getInDomain('playback.utils.createGetStationByMusicIdSelector', noop)(state)(
    musicId,
  );
  const stationId = station && station.get('stationId');
  const isPlayingCurrently = stationId === getInDomain('playback.utils.getCurrentStationId')(state);
  const isAudioPlaying = getInDomain('playback.utils.isAudioPlaying')(state);

  return {
    stationId,
    genreName: genre.get('name') || '',
    seoToken: genre.get('token'),
    imgUrl: getArt(genre, GridConstants.GRID_ITEM_IMAGE_SIZE, state),
    isPlaying: isPlayingCurrently && isAudioPlaying,
    isPaused: !isAudioPlaying && isPlayingCurrently,
    shouldResumePlayback:
      isPlayingCurrently && getInDomain('playback.utils.getCurrentTrackTime')(state) > 0,
    isAnonymous: isAnonymousListener(state),
    isAdvertiserStation: getInDomain('playback.utils.getStationIsAdvertiser', false)(
      state,
      ownProps.stationId,
    ),
    isOwnStation: getInDomain('playback.utils.isOwnStation', false)(state, stationId),
  };
}

const mapDispatchToProps = dispatch => {
  return bindActionCreators(
    {
      startStation: Actions.UseCases.startStation,
      pauseTrack: Actions.UseCases.pauseTrack,
      playResumeSource: Actions.UseCases.playResumeSource,
      logWebRegistrationEvent: getInDomain('analytics.actions.logWebRegistrationEvent', noop),
    },
    dispatch,
  );
};

export default connect(mapStateToProps, mapDispatchToProps)(GenreStationGridItem);
